import React, { useState, useEffect } from "react";
import {
  X,
  Scale,
  ShieldCheck,
  HeartHandshake,
  AlertTriangle,
  FileText,
  PhoneCall,
  ExternalLink,
  Info
} from "lucide-react";

export function LegalModal({ isOpen, onClose, initialTab = "terms" }) {
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab, isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-gray-900 border border-gray-800 rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-800 flex items-center justify-between bg-gray-950/60">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 border border-purple-500/20 rounded-xl text-purple-400">
              <Scale className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-white">
                Legal & Compliance Center
              </h2>
              <p className="text-xs text-gray-400">
                Official transparency, regulatory disclaimers, and player policies
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/80 transition-colors"
            aria-label="Close legal modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-800 bg-gray-950/40 px-3 sm:px-6 overflow-x-auto no-scrollbar gap-1 sm:gap-2 pt-2">
          <button
            onClick={() => setActiveTab("terms")}
            className={`flex items-center gap-2 px-3 py-2.5 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap transition-all ${
              activeTab === "terms"
                ? "border-purple-500 text-white font-semibold"
                : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            <FileText className="h-4 w-4" />
            <span>Terms & Conditions</span>
          </button>

          <button
            onClick={() => setActiveTab("privacy")}
            className={`flex items-center gap-2 px-3 py-2.5 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap transition-all ${
              activeTab === "privacy"
                ? "border-blue-500 text-white font-semibold"
                : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            <ShieldCheck className="h-4 w-4" />
            <span>Privacy Policy</span>
          </button>

          <button
            onClick={() => setActiveTab("responsible")}
            className={`flex items-center gap-2 px-3 py-2.5 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap transition-all ${
              activeTab === "responsible"
                ? "border-emerald-500 text-white font-semibold"
                : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            <HeartHandshake className="h-4 w-4 text-emerald-400" />
            <span className="flex items-center gap-1.5">
              <span>Responsible Gaming</span>
              <span className="px-1.5 py-0.2 bg-emerald-500/20 text-emerald-300 text-[10px] font-bold rounded">
                21+
              </span>
            </span>
          </button>

          <button
            onClick={() => setActiveTab("disclaimer")}
            className={`flex items-center gap-2 px-3 py-2.5 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap transition-all ${
              activeTab === "disclaimer"
                ? "border-amber-500 text-white font-semibold"
                : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <span>Data Disclaimer</span>
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 text-sm text-gray-300 space-y-6 leading-relaxed">
          {/* TERMS & CONDITIONS */}
          {activeTab === "terms" && (
            <div className="space-y-5 animate-in fade-in duration-150">
              <div className="border-b border-gray-800 pb-3">
                <h3 className="text-base font-bold text-white">Terms & Conditions of Service</h3>
                <p className="text-xs text-gray-400 mt-1">Last Updated: September 2026</p>
              </div>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">1. Informational & Statistical Purpose Only</h4>
                <p className="text-gray-300">
                  Casino Figures (<span className="text-purple-400">casinofigures.com</span>) provides statistical aggregation, historical performance metrics, and analytical tools for informational and educational purposes only. 
                  <strong> Casino Figures is NOT a casino, does not provide gambling services, and does not accept or place bets, wagers, or financial transactions of any kind.</strong>
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">2. No Gambling or Financial Advice</h4>
                <p className="text-gray-300">
                  All metrics displayed—including Return to Player (RTP %), casino hold rates, historical gross revenue, and denomination yields—represent historical aggregate public data reported to regulatory gaming commissions. None of the materials or calculations on this website constitute financial advice, gambling strategy, or a prediction of personal gaming outcomes.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">3. User Accounts & Access</h4>
                <p className="text-gray-300">
                  By creating an account or accessing Casino Figures, you agree to:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-gray-300">
                  <li>Be at least 21 years of age, or the legal age of majority in your jurisdiction.</li>
                  <li>Provide accurate, current account details and maintain the security of your credentials.</li>
                  <li>Refrain from scraping, data harvesting, bot automated extraction, or attempting to compromise platform infrastructure.</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">4. Intellectual Property</h4>
                <p className="text-gray-300">
                  The visual layout, design, comparison algorithms, proprietary rankings, and interface formatting of Casino Figures are protected by copyright, trademark, and intellectual property laws. Public regulatory filings remain the property of their respective state commissions.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">5. Limitation of Liability</h4>
                <p className="text-gray-300">
                  Casino Figures, its officers, employees, and affiliates will not be liable for any direct, indirect, incidental, or consequential damages resulting from the use of our platform, including any monetary losses incurred at commercial, tribal, or online casino properties.
                </p>
              </section>
            </div>
          )}

          {/* PRIVACY POLICY */}
          {activeTab === "privacy" && (
            <div className="space-y-5 animate-in fade-in duration-150">
              <div className="border-b border-gray-800 pb-3">
                <h3 className="text-base font-bold text-white">Privacy Policy</h3>
                <p className="text-xs text-gray-400 mt-1">Last Updated: September 2026</p>
              </div>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">1. Information We Collect</h4>
                <p className="text-gray-300">
                  We respect your privacy and only collect information essential for delivering our services:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-gray-300">
                  <li><strong>Account Credentials:</strong> When you register, we securely store your email address and profile name via encrypted authentication (Supabase Auth).</li>
                  <li><strong>Usage & Preferences:</strong> Anonymized platform interactions, selected time periods, and perspective toggle preferences (Player RTP vs Casino Hold).</li>
                  <li><strong>Technical Logs:</strong> IP address, device browser type, and page access timestamps for security, fraud prevention, and performance monitoring.</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">2. How We Use Information</h4>
                <p className="text-gray-300">
                  Your information is utilized strictly to provide personalized dashboard access, maintain authenticated sessions, improve system response times, and communicate vital product updates.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">3. We Do Not Sell Your Data</h4>
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-200">
                  <strong>Zero Data Brokerage:</strong> We do not sell, rent, or monetize your personal information to third-party marketing brokers or casino operators.
                </div>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">4. Cookies & Local Storage</h4>
                <p className="text-gray-300">
                  We use browser session storage and local cookies solely to remember your login session and dashboard display preferences. You can manage cookie controls directly in your browser settings.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">5. Your Rights & Data Deletion</h4>
                <p className="text-gray-300">
                  Under applicable laws (including CCPA and GDPR), you have the right to request a copy of your stored data or request the permanent deletion of your account. Contact us at <span className="text-purple-400 underline">support@casinofigures.com</span>.
                </p>
              </section>
            </div>
          )}

          {/* RESPONSIBLE GAMING */}
          {activeTab === "responsible" && (
            <div className="space-y-5 animate-in fade-in duration-150">
              <div className="border-b border-gray-800 pb-3 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <span>Responsible Gaming Commitment</span>
                    <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded-full border border-emerald-500/30">
                      Age 21+ Only
                    </span>
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">Player safety, awareness, and national assistance resources</p>
                </div>
              </div>

              {/* Emergency Banner */}
              <div className="p-4 bg-gradient-to-r from-red-950/50 to-amber-950/40 border border-red-500/40 rounded-xl text-white">
                <div className="flex items-start gap-3">
                  <PhoneCall className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-base text-red-200">
                      Need Help? Free, Confidential 24/7 Support
                    </h4>
                    <p className="text-sm text-gray-200 mt-1">
                      If you or someone you know has a gambling problem, crisis counseling and referral services can be accessed immediately:
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <a
                        href="tel:18004262537"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg text-sm shadow transition-colors"
                      >
                        <PhoneCall className="h-4 w-4" />
                        Call 1-800-GAMBLER (1-800-426-2537)
                      </a>
                      <a
                        href="https://www.800gambler.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-amber-300 hover:text-white underline"
                      >
                        Visit 800gambler.org
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">Principles of Responsible Play</h4>
                <p className="text-gray-300">
                  Gambling should always be considered entertainment, not a source of income or a method for solving financial hardship. Remember:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 text-gray-300">
                  <li><strong>The Math Advantage:</strong> In every casino game, the house maintains a mathematical mathematical edge over the long run. Statistical RTP percentages do not guarantee short-term individual profits.</li>
                  <li><strong>Set Time & Money Budgets:</strong> Determine your loss limits before you play and never chase losses.</li>
                  <li><strong>Never Borrow to Gamble:</strong> Only play with discretionary entertainment funds you can comfortably afford to lose.</li>
                  <li><strong>Balance Your Life:</strong> Ensure gaming never interferes with your relationships, work, or family obligations.</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">Recognized Support Organizations</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div className="p-3 bg-gray-800/60 rounded-xl border border-gray-700">
                    <p className="font-semibold text-white text-xs">National Council on Problem Gambling</p>
                    <p className="text-xs text-gray-400 mt-1">Toll-free 24/7 confidential helpline: 1-800-522-4700</p>
                    <a
                      href="https://www.ncpgambling.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:underline mt-2"
                    >
                      ncpgambling.org <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <div className="p-3 bg-gray-800/60 rounded-xl border border-gray-700">
                    <p className="font-semibold text-white text-xs">Gamblers Anonymous</p>
                    <p className="text-xs text-gray-400 mt-1">Peer fellowship and 12-step recovery program</p>
                    <a
                      href="https://www.gamblersanonymous.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:underline mt-2"
                    >
                      gamblersanonymous.org <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">State Self-Exclusion Programs</h4>
                <p className="text-gray-300">
                  Most regulated jurisdictions provide voluntary self-exclusion registries enabling individuals to ban themselves from entering physical gaming properties or accessing legal internet gaming accounts. For New Jersey residents and visitors, visit the <a href="https://www.nj.gov/oag/ge/self_exclusion.html" target="_blank" rel="noopener noreferrer" className="text-purple-400 underline">NJ Division of Gaming Enforcement Self-Exclusion Portal</a>.
                </p>
              </section>
            </div>
          )}

          {/* DATA & REGULATORY DISCLAIMER */}
          {activeTab === "disclaimer" && (
            <div className="space-y-5 animate-in fade-in duration-150">
              <div className="border-b border-gray-800 pb-3">
                <h3 className="text-base font-bold text-white">Data & Regulatory Disclaimer</h3>
                <p className="text-xs text-gray-400 mt-1">Transparency on our figures, sources, and statistical methodology</p>
              </div>

              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-2.5">
                <Info className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-200">
                  All figures shown on Casino Figures are aggregated strictly from official public regulatory filings, such as the monthly and annual reporting published by the <strong>State of New Jersey Division of Gaming Enforcement (DGE)</strong>.
                </p>
              </div>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">1. Understanding RTP and Casino Hold</h4>
                <p className="text-gray-300">
                  <strong>Casino Hold %</strong> is the percentage of total wagered handle (or table drop) retained by the casino operator as gross revenue. 
                  <strong> Return to Player (RTP %)</strong> is calculated as <code className="px-1.5 py-0.5 bg-gray-800 text-purple-300 rounded font-mono text-xs">100% - Hold %</code>.
                </p>
                <p className="text-gray-300 text-xs leading-relaxed">
                  These percentages reflect aggregate statistical outcomes over hundreds of millions of wagers across all machines or tables within a casino during the specified time period. Independent probabilities mean that an individual player's session can deviate drastically from the aggregate average.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">2. Independent Entity Notice</h4>
                <p className="text-gray-300">
                  Casino Figures is an independent market intelligence and consumer analytics platform. We are not owned, affiliated with, sponsored by, or endorsed by any licensed casino operator, resort holding company, or government gaming regulatory body.
                </p>
              </section>

              <section className="space-y-2">
                <h4 className="font-semibold text-white">3. Timeliness & Accuracy of Public Records</h4>
                <p className="text-gray-300">
                  While Casino Figures makes every reasonable effort to ingest, sanitize, and verify regulatory records promptly following official release, regulatory agencies may periodically issue revised or restated audit reports. Casino Figures does not warrant uninterrupted, error-free presentation of public datasets.
                </p>
              </section>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-800 bg-gray-950/80 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <span>Dedicated to transparent data & player well-being.</span>
          </div>
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-5 py-2 bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-xl text-xs transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default LegalModal;
